// import classNames from 'classnames';
import styles from './MatchList.module.css';
import { Match } from 'components/Match/Match';
import { FULL_TIME_BETGROUP_NAME } from 'config/constants';
import { LeagueContext } from 'context/leagueContext';
import {
  translateBetMarketName,
  translateBetMarketNameForSelection,
} from 'helpers/betI18n';
import { getAllBets } from 'helpers/matchHelper';
import { Bet } from 'types/Bet';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeIcon, ThemeIconFromSrc } from 'components/ThemeIcon/ThemeIcon';
import { MatchOffer } from 'types/MatchOffer';

interface Props {
  leagueId?: number;
  leagueName: string;
  events: MatchOffer[];
  icon?: string;
  cssStyles?: {
    marginTop?: string;
  };
  hideBetFilter?: boolean;
  searchResults?: boolean;
}

export const MatchList = ({
  leagueId,
  leagueName,
  events,
  icon = '',
  cssStyles,
  hideBetFilter = false,
  searchResults = false,
}: Props) => {
  const [betsExpanted, setBetsExpanded] = useState(false);
  const [selectedBetName, setSelectedBetName] = useState<string | null>(null);
  const { deselectLeague } = useContext(LeagueContext);
  const { t } = useTranslation();

  const eventWithMostBets = useMemo(() => {
    if (!events || events.length === 0) return null;
    return [...events].sort((x, y) => {
      const betsXCount = getAllBets(x).length;
      const betsYCount = getAllBets(y).length;
      return betsYCount - betsXCount;
    })[0];
  }, [events]);

  const allBets: Bet[] = useMemo(() => {
    if (!eventWithMostBets) return [];
    return getAllBets(eventWithMostBets, true).filter((b): b is Bet =>
      Boolean(b && b.name)
    );
  }, [eventWithMostBets]);

  const hasFullTime1x2 = useMemo(
    () =>
      events.some(
        (e) =>
          Array.isArray(e.fullTimeBet?.odds) && e.fullTimeBet.odds.length > 0
      ),
    [events]
  );

  const handleBetOptionClick = (name: string) => {
    setSelectedBetName(name);
    setBetsExpanded(false);
  };

  return (
    <div
      className={styles.container}
      style={{ marginTop: cssStyles?.marginTop }}
    >
      {!searchResults && (
        <div className={styles.header}>
          {leagueName ?? events[0].league.name}
          {icon && (
            <ThemeIconFromSrc
              src={icon}
              alt={events[0].league?.name ?? ''}
              variant="inline"
              tone="chrome"
            />
          )}
          {!hideBetFilter && (
            <div
              className={styles.betFilter}
              onClick={() => setBetsExpanded(!betsExpanted)}
            >
              {t('other_bet_types_label')} ({allBets.length})
              <ThemeIcon
                icon="chevron-down"
                alt="Expand/Collapse"
                variant="inline"
                tone="chrome"
              />
            </div>
          )}
          <ThemeIcon
            icon="remove"
            alt="Remove"
            variant="inline"
            tone="chrome"
            className={styles.closeButton}
            onClick={() => deselectLeague(leagueId)}
          />
        </div>
      )}
      <div className={styles.labelsWrapper}>
        {betsExpanted && (
          <div className={styles.betsSelection}>
            {allBets &&
              allBets.length > 0 &&
              allBets.map((x, index) => (
                <div
                  key={index}
                  className={styles.betOption}
                  onClick={() => handleBetOptionClick(x.name)}
                >
                  {translateBetMarketName(x, t)} ({x.odds?.length})
                </div>
              ))}
          </div>
        )}
        {!hideBetFilter && (
          <div className={styles.leagueAndBet}>
            {leagueName}
            {!searchResults && (
              <>
                <ThemeIcon
                  icon="arrow-right-white"
                  alt="Right Arrow"
                  variant="inline"
                  tone="chrome"
                />
                {translateBetMarketNameForSelection(
                  selectedBetName ?? FULL_TIME_BETGROUP_NAME,
                  allBets,
                  t
                )}
                <ThemeIcon
                  icon="remove"
                  alt="Remove"
                  variant="inline"
                  tone="chrome"
                  className={styles.closeButton}
                  onClick={() => deselectLeague(leagueId)}
                />
              </>
            )}
          </div>
        )}
        {!selectedBetName && hasFullTime1x2 && (
          <div className={styles.labels1x2}>
            <div>{t('bet_col_1x2_home')}</div>
            <div>{t('bet_col_1x2_draw')}</div>
            <div>{t('bet_col_1x2_away')}</div>
          </div>
        )}
      </div>
      {events &&
        events.length > 0 &&
        events
          .sort((a, b) => a.time - b.time)
          .map((e, index) => (
            <Match
              match={e}
              sportId={e.league.sport}
              key={e.fi}
              betName={selectedBetName!}
              noBg={index % 2 === 1}
            />
          ))}
    </div>
  );
};
